'use client';

import React, { Suspense, useState, useEffect } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import styles from '../styles/Home.module.css';
import { useAuth } from '@/lib/AuthContext';
import { Navbar } from '@/components/Navbar';
import { MeetingEntry } from '@/components/MeetingEntry';
import { UpcomingMeetings } from '@/components/UpcomingMeetings';
import { Login } from '@/components/LoginForm';
import { Register } from '@/components/RegisterForm';
import { ForgotPassword } from '@/components/ForgotPassword';
import { ResetPassword } from '@/components/ResetPassword';
import { NepaliDateCard } from '@/components/NepaliDate';

type AuthView = 'login' | 'register' | 'forgot-password' | 'reset-password';

function PageContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { user, loading, token } = useAuth();
  const [authView, setAuthView] = useState<AuthView>('login');
  const [callbackUrl, setCallbackUrl] = useState<string | null>(null);

  useEffect(() => {
    const callback = searchParams.get('callbackUrl');
    if (callback) {
      setCallbackUrl(decodeURIComponent(callback));
    }
  }, [searchParams]);


  // Redirect to callback URL after successful login
  useEffect(() => {
    if (user && token && callbackUrl) {
      router.push(callbackUrl);
    }
  }, [user, token, callbackUrl, router]);

  if (loading) {
    return (
      <div className={styles.loadingScreen}>
        Loading...
      </div>
    );
  }

  // Show reset password component when on reset-password view
  if (authView === 'reset-password') {
    return <ResetPassword />;
  }

  // Show auth components when not logged in
  if (!user || !token) {
    if (authView === 'register') {
      return <Register onSwitchToLogin={() => setAuthView('login')} />;
    }
    if (authView === 'forgot-password') {
      return <ForgotPassword onBackToLogin={() => setAuthView('login')} />;
    }
    return (
      <Login
        onSwitchToRegister={() => setAuthView('register')}
        onForgotPassword={() => setAuthView('forgot-password')}
      />
    );
  }

  const getUserName = () => {
    if (user?.name) return user.name;
    if (user?.email) return user.email.split('@')[0];
    return 'User';
  };

  return (
    <>
      <Navbar />
      <main className={styles.main} data-lk-theme="default">
        {/* Dashboard Header */}
        <div className={styles.dashboardHeader}>
          <div className={styles.welcomeSection}>
            <h1>Welcome back, {getUserName()}!</h1>
          </div>
          <NepaliDateCard />
        </div>

        {/* Main Content */}
        <div className={styles.contentGrid}>
          <div className={styles.sectionCard}>
            <div className={styles.sectionHeader}>
              <h2>Quick Start</h2>
            </div>
            <MeetingEntry />
          </div>

          <div className={styles.sectionCard}>
            <div className={styles.sectionHeader}>
              <h2>Upcoming Meetings</h2>
            </div>
            <UpcomingMeetings />
          </div>
        </div>
      </main>
    </>
  );
}

export default function Page() {
  return (
    <Suspense fallback={
      <div className={styles.loadingScreen}>
        Loading...
      </div>
    }>
      <PageContent />
    </Suspense>
  );
}
